import { Add, Delete } from '@mui/icons-material';
import { Box, Typography, Button, IconButton, Paper, Divider } from '@mui/material';
import { Formik, Form, FieldArray } from 'formik';
import { useMemo } from 'react';

import FormSkeleton from './formSkeleton';
import FormField from 'components/common/formikForm/formField';
import { FormBusyProvider } from 'components/common/formikForm/formBusyContext';
import FormGridLayout from 'components/common/formikForm/formGridLayout';
import { StepNavigation } from 'components/v2/StepNavigation';

import { BooleanValue } from 'constant';
import {
    useCountries,
    useNationalities,
    useRelations,
    useCountryResidentialStatus,
    useRegions,
    useRelativesAbroad,
    useSaveRelativesAbroad,
} from 'hooks';
import useEnabledCountries from 'hooks/useEnabledCountries';
import useTranslate from 'hooks/useTranslate';
import type { RelativeAbroad } from 'types';
import { chosenCountryName, countryNameOptions, relativesQuestionLabelKey } from 'utils/countries';
import { generateOptionFromObject, generateOptionsFromString } from 'utils/dataFormatter';
import { relativesAbroadValidationSchema } from 'utils/validations/relativesAbroadSchema';

const booleanOptions = generateOptionFromObject<boolean>(BooleanValue);

/** Age 1–112 options (AssessmentV2 RelativesAbroadForm). */
const ageOptions = generateOptionsFromString(Array.from({ length: 112 }, (_, i) => String(i + 1)));

/** UK marital status is only Married / Single in the old form. */
const ukMaritalStatusOptions = generateOptionsFromString(['Married', 'Single']);

const emptyRelative: Partial<RelativeAbroad> = {
    age: null,
    country: '',
    country_of_birth: '',
    marital_status: '',
    nationality: '',
    region: '',
    relation: '',
    residence_since: undefined,
    residency_status: '',
};

interface RelativesAbroadStepProps {
    isLastStep: boolean;
    formData: Record<string, unknown>;
    onNext: (data: Record<string, unknown>) => void;
    onBack: () => void;
}

const RelativesAbroadStep = ({ onNext, onBack, isLastStep }: RelativesAbroadStepProps) => {
    const { translate } = useTranslate();
    const { codes, hasSelfServeMarket, has } = useEnabledCountries();
    const { data: relativesData, isLoading } = useRelativesAbroad();
    const saveRelatives = useSaveRelativesAbroad();

    const blankRelative = (): Partial<RelativeAbroad> => ({
        ...emptyRelative,
        country: chosenCountryName(codes) ?? '',
    });

    // Full country list for UK-only birth country
    const { data: countries = [] } = useCountries();
    const { data: nationalities = [] } = useNationalities();
    const { data: regions } = useRegions();
    const { data: relations = [] } = useRelations();

    const allCountryOptions = generateOptionsFromString(countries || []);
    // AssessmentV2: UK nationality list when available, else full countries list
    const nationalityOptions = generateOptionsFromString(
        nationalities && nationalities.length > 0 ? nationalities : countries || []
    );
    const relationOptions = generateOptionsFromString(relations || []);

    const hasRelativesLabelKey = useMemo(() => relativesQuestionLabelKey(codes), [codes]);

    const initialValues = {
        has_relatives: Boolean(relativesData && relativesData.length > 0),
        relatives: relativesData || [],
    };

    const handleSubmit = async (values: {
        has_relatives?: boolean;
        relatives: RelativeAbroad[];
    }) => {
        try {
            const payload = values.has_relatives ? values.relatives || [] : [];
            await saveRelatives.mutateAsync(payload);
            onNext({ relatives: payload });
        } catch {
            // Error alert is handled by the mutation hook onError
        }
    };

    if (isLoading) {
        return <FormSkeleton fields={10} />;
    }

    return (
        <Box>
            <Formik
                initialValues={initialValues}
                validationSchema={relativesAbroadValidationSchema}
                onSubmit={handleSubmit}
                enableReinitialize
            >
                {({ values, isValid, isSubmitting, setFieldValue, setValues }) => {
                    const isSaving = isSubmitting || saveRelatives.isPending;

                    return (
                        <FormBusyProvider busy={isSaving}>
                            <Form>
                                <FormField
                                    type="radio"
                                    row
                                    name="has_relatives"
                                    label={hasRelativesLabelKey}
                                    options={booleanOptions}
                                    onChange={(value: unknown) => {
                                        const boolValue = value === true || value === 'true';
                                        return setValues(
                                            {
                                                ...values,
                                                has_relatives: boolValue,
                                                relatives: boolValue
                                                    ? [blankRelative() as RelativeAbroad]
                                                    : [],
                                            },
                                            true
                                        );
                                    }}
                                />

                                {values.has_relatives && (
                                    <FieldArray name="relatives">
                                        {({ push, remove }) => (
                                            <Box>
                                                {values.relatives.map((relative, index) => {
                                                    const destinationCountryOptions =
                                                        generateOptionsFromString(
                                                            countryNameOptions(
                                                                codes,
                                                                relative.country
                                                            )
                                                        );
                                                    const regionOptions = generateOptionsFromString(
                                                        relative.country
                                                            ? (
                                                                  regions as Record<
                                                                      string,
                                                                      string[]
                                                                  > | null
                                                              )?.[relative.country] || []
                                                            : []
                                                    );

                                                    return (
                                                        <Paper
                                                            key={index}
                                                            elevation={1}
                                                            sx={{
                                                                p: { xs: 2, sm: 3 },
                                                                mb: 1,
                                                                mt: 2,
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    justifyContent: 'space-between',
                                                                    alignItems: 'center',
                                                                    mb: 2,
                                                                }}
                                                            >
                                                                <Typography
                                                                    variant="h6"
                                                                    sx={{ fontSize: '1.1rem' }}
                                                                >
                                                                    {translate('relative')}{' '}
                                                                    {index + 1}
                                                                </Typography>
                                                                {values.relatives.length > 1 && (
                                                                    <IconButton
                                                                        onClick={() =>
                                                                            remove(index)
                                                                        }
                                                                        color="error"
                                                                        size="small"
                                                                        aria-label="Remove relative"
                                                                    >
                                                                        <Delete />
                                                                    </IconButton>
                                                                )}
                                                            </Box>
                                                            <Divider sx={{ mb: 2 }} />

                                                            <FormGridLayout>
                                                                {/* Always: relation + country (destination) */}
                                                                <FormField
                                                                    required
                                                                    type="autosuggest"
                                                                    name={`relatives.${index}.relation`}
                                                                    label="relation"
                                                                    options={relationOptions}
                                                                />
                                                                <FormField
                                                                    required
                                                                    type="autosuggest"
                                                                    name={`relatives.${index}.country`}
                                                                    label="country"
                                                                    options={
                                                                        destinationCountryOptions
                                                                    }
                                                                    onChange={(value) => {
                                                                        // AssessmentV2: clear region + residency when country changes
                                                                        setFieldValue(
                                                                            `relatives.${index}.country`,
                                                                            value
                                                                        );
                                                                        setFieldValue(
                                                                            `relatives.${index}.region`,
                                                                            ''
                                                                        );
                                                                        setFieldValue(
                                                                            `relatives.${index}.residency_status`,
                                                                            ''
                                                                        );
                                                                    }}
                                                                />

                                                                {/* UK + AU: birth */}
                                                                {hasSelfServeMarket && (
                                                                    <FormField
                                                                        type="autosuggest"
                                                                        name={`relatives.${index}.country_of_birth`}
                                                                        label="countryOfBirth"
                                                                        options={allCountryOptions}
                                                                    />
                                                                )}

                                                                {/* UK domain only: marital, nationality */}
                                                                {has('UK') && (
                                                                    <>
                                                                        <FormField
                                                                            type="radio"
                                                                            row
                                                                            name={`relatives.${index}.marital_status`}
                                                                            label="maritalStatus"
                                                                            options={
                                                                                ukMaritalStatusOptions
                                                                            }
                                                                        />
                                                                        <FormField
                                                                            type="autosuggest"
                                                                            name={`relatives.${index}.nationality`}
                                                                            label="nationality"
                                                                            options={
                                                                                nationalityOptions
                                                                            }
                                                                        />
                                                                    </>
                                                                )}

                                                                {/* Always: region + residency + residence since + age */}
                                                                <FormField
                                                                    required
                                                                    type="autosuggest"
                                                                    name={`relatives.${index}.region`}
                                                                    label="region"
                                                                    options={regionOptions}
                                                                    disabled={!relative.country}
                                                                />
                                                                <RelativeResidencyStatusField
                                                                    index={index}
                                                                    country={relative.country || ''}
                                                                />
                                                                <FormField
                                                                    type="monthyear"
                                                                    name={`relatives.${index}.residence_since`}
                                                                    label="residenceStatusSince"
                                                                />
                                                                <FormField
                                                                    type="autosuggest"
                                                                    name={`relatives.${index}.age`}
                                                                    label="age"
                                                                    options={ageOptions}
                                                                />
                                                            </FormGridLayout>
                                                        </Paper>
                                                    );
                                                })}

                                                {values.has_relatives &&
                                                    values.relatives.length < 10 && (
                                                        <Button
                                                            variant="outlined"
                                                            startIcon={<Add />}
                                                            onClick={() =>
                                                                push(
                                                                    blankRelative() as RelativeAbroad
                                                                )
                                                            }
                                                            fullWidth
                                                            sx={{ mb: 1, mt: 2 }}
                                                        >
                                                            {translate('addRelative')}
                                                        </Button>
                                                    )}
                                            </Box>
                                        )}
                                    </FieldArray>
                                )}

                                <StepNavigation
                                    isLastStep={isLastStep}
                                    onBack={onBack}
                                    isSubmitting={isSaving}
                                    disableNext={!isValid}
                                />
                            </Form>
                        </FormBusyProvider>
                    );
                }}
            </Formik>
        </Box>
    );
};

export default RelativesAbroadStep;

const RelativeResidencyStatusField = ({ index, country }: { index: number; country: string }) => {
    const { data, isFetching } = useCountryResidentialStatus(country);
    const residencyStatusOptions = generateOptionsFromString(data || []);

    return (
        <FormField
            required
            type="autosuggest"
            name={`relatives.${index}.residency_status`}
            label="residenceStatus"
            options={residencyStatusOptions}
            disabled={!country || isFetching}
        />
    );
};
